import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import "./EditServer.css";

const EditServer = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // এডিটেবল ফিল্ড: শুধু display name + lan_ip
  const [formData, setFormData] = useState({
    server_name: "",
    lan_ip: "",
  });

  // রিড-অনলি লাইভ মেট্রিক্স (heartbeat থেকে আসে)
  const [metrics, setMetrics] = useState({
    uuid: "",
    hostname: "",
    os_info: "",
    mac_address: "",
    public_ip: "",
    cpu_usage: "",
    ram_usage: "",
    disk_usage: "",
    uptime: "",
    status: "",
    last_checked: "",
  });

  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const fetchServerDetails = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(`/api/servers/details/${id}`);

      if (res.data?.status === "success" && res.data?.data) {
        const s = res.data.data;
        setFormData({
          server_name: s.server_name || "",
          lan_ip: s.lan_ip || "",
        });
        setMetrics({
          uuid: s.uuid || "N/A",
          hostname: s.hostname || "N/A",
          os_info: s.os_info || "N/A",
          mac_address: s.mac_address || "N/A",
          public_ip: s.public_ip || "N/A",
          cpu_usage: s.cpu_usage ?? "0.00",
          ram_usage: s.ram_usage ?? "0.00",
          disk_usage: s.disk_usage ?? "0.00",
          uptime: s.uptime || "N/A",
          status: s.status || "offline",
          last_checked: s.last_checked || "N/A",
        });
      } else {
        setError("Failed to parse cluster node details response.");
      }
    } catch (err) {
      console.error("Error fetching single server matrix:", err);
      setError(err.response?.data?.message || "Error establishing connection with gateway registry.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServerDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.server_name.trim()) {
      setError("Server Name is required.");
      return;
    }

    try {
      setSubmitLoading(true);
      setError("");
      setSuccessMsg("");

      const res = await axios.put(`/api/servers/update/${id}`, formData);

      if (res.data?.status === "success") {
        setSuccessMsg("Server node updated! 🚀");
        setTimeout(() => navigate("/manage-server"), 1800);
      }
    } catch (err) {
      console.error("Error updating server configuration matrix:", err);
      setError(err.response?.data?.message || "Failed to patch server configuration parameters.");
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px", gap: "12px" }}>
        <div style={{ width: "30px", height: "30px", border: "3px solid #f3f3f3", borderTop: "3px solid #0f172a", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
        <p style={{ color: "#64748b", fontSize: "14px" }}>Pulling node parameters...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: "680px", margin: "0 auto", fontFamily: "sans-serif" }}>

      <div style={{ marginBottom: "28px", borderBottom: "1px solid #e2e8f0", paddingBottom: "16px" }}>
        <h2 style={{ margin: 0, fontSize: "22px", color: "#1e293b", fontWeight: "800" }}>Server Node Details</h2>
        <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: "14px" }}>
          Live telemetry (read-only, from heartbeat) · edit display name & LAN IP.
        </p>
      </div>

      {error && <div style={{ background: "#fee2e2", color: "#991b1b", padding: "12px 16px", borderRadius: "6px", marginBottom: "20px", fontSize: "14px", fontWeight: "600", border: "1px solid #fca5a5" }}>⚠️ {error}</div>}
      {successMsg && <div style={{ background: "#dcfce7", color: "#166534", padding: "12px 16px", borderRadius: "6px", marginBottom: "20px", fontSize: "14px", fontWeight: "600", border: "1px solid #86efac" }}>{successMsg}</div>}

      <form onSubmit={handleSubmit} style={{ background: "#ffffff", padding: "24px", borderRadius: "8px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", display: "flex", flexDirection: "column", gap: "20px" }}>

        {/* READ-ONLY LIVE METRICS */}
        <div style={{ background: "#f1f5f9", padding: "16px", borderRadius: "6px", border: "1px solid #e2e8f0", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", fontSize: "13px" }}>
          <div style={{ gridColumn: "span 2" }}>
            <span style={{ color: "#64748b" }}>Machine UUID:</span>
            <div style={{ fontFamily: "monospace", color: "#2563eb", fontWeight: "600", marginTop: "2px", wordBreak: "break-all" }}>{metrics.uuid}</div>
          </div>
          <div>
            <span style={{ color: "#64748b" }}>Hostname:</span>
            <div style={{ fontFamily: "monospace", color: "#334155", fontWeight: "600", marginTop: "2px" }}>{metrics.hostname}</div>
          </div>
          <div>
            <span style={{ color: "#64748b" }}>OS:</span>
            <div style={{ fontFamily: "monospace", color: "#334155", fontWeight: "600", marginTop: "2px" }}>{metrics.os_info}</div>
          </div>
          <div>
            <span style={{ color: "#64748b" }}>MAC:</span>
            <div style={{ fontFamily: "monospace", color: "#334155", fontWeight: "600", marginTop: "2px" }}>{metrics.mac_address}</div>
          </div>
          <div>
            <span style={{ color: "#64748b" }}>Public IP:</span>
            <div style={{ fontFamily: "monospace", color: "#334155", fontWeight: "600", marginTop: "2px" }}>{metrics.public_ip}</div>
          </div>
          <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "8px" }}>
            <span style={{ color: "#64748b" }}>Status:</span>
            <div style={{ marginTop: "4px" }}>
              <span style={{ background: metrics.status === "active" ? "#dcfce7" : "#fee2e2", color: metrics.status === "active" ? "#15803d" : "#b91c1c", padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase" }}>
                {metrics.status === "active" ? "online" : "offline"}
              </span>
            </div>
          </div>
          <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "8px" }}>
            <span style={{ color: "#64748b" }}>Uptime:</span>
            <div style={{ fontFamily: "monospace", color: "#475569", fontWeight: "600", marginTop: "2px" }}>{metrics.uptime}</div>
          </div>
          <div style={{ gridColumn: "span 2", borderTop: "1px solid #e2e8f0", paddingTop: "8px" }}>
            <span style={{ color: "#64748b" }}>Live Resource Load:</span>
            <div style={{ fontFamily: "monospace", fontWeight: "700", color: "#334155", marginTop: "2px" }}>
              CPU: {metrics.cpu_usage}% &nbsp;|&nbsp; RAM: {metrics.ram_usage}% &nbsp;|&nbsp; Disk: {metrics.disk_usage}%
            </div>
          </div>
        </div>

        {/* EDITABLE: SERVER NAME */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label style={{ fontSize: "14px", fontWeight: "700", color: "#334155" }}>Server / Client Display Name</label>
          <input
            type="text"
            name="server_name"
            value={formData.server_name}
            onChange={handleChange}
            placeholder="e.g., Shanto (ChitroboxBD)"
            style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "14px", outline: "none" }}
            required
          />
        </div>

        {/* EDITABLE: LAN IP */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label style={{ fontSize: "14px", fontWeight: "700", color: "#334155" }}>LAN IP (display record)</label>
          <input
            type="text"
            name="lan_ip"
            value={formData.lan_ip}
            onChange={handleChange}
            placeholder="e.g., 192.168.253.10"
            style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "14px", fontFamily: "monospace", outline: "none" }}
          />
        </div>

        {/* ACTIONS */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "8px", borderTop: "1px solid #f1f5f9", paddingTop: "16px" }}>
          <button
            type="button"
            onClick={() => navigate("/manage-server")}
            style={{ padding: "10px 20px", borderRadius: "6px", border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer", fontWeight: "bold", color: "#475569", fontSize: "14px" }}
          >
            ← Back to List
          </button>
          <button
            type="submit"
            disabled={submitLoading}
            style={{ padding: "10px 24px", borderRadius: "6px", border: "none", background: submitLoading ? "#94a3b8" : "#0f172a", color: "#fff", fontWeight: "bold", cursor: submitLoading ? "not-allowed" : "pointer", fontSize: "14px" }}
          >
            {submitLoading ? "Saving..." : "💾 Save Changes"}
          </button>
        </div>

      </form>
    </div>
  );
};

export default EditServer;